/**
 * Document Workspace API
 * 
 * Functions for creating and managing document workspaces,
 * including AI-powered document generation.
 */

import { authenticatedFetch } from '../utils/authApi';
import { actionsMenuCache } from '../components/ActionsMenu/ActionsMenu';
import { buildApiUrl } from '../config/apiConfig';

const API_BASE = '/api/document-workspaces';

// ============================================================================
// TYPES
// ============================================================================

export interface WorkspaceSection {
    id: string;
    title: string;
    level: number;
    content: string;
    status: 'draft' | 'approved' | 'ai_generated' | 'ai_updating' | 'user_editing';
    order: number;
    createdAt: string;
    updatedAt: string;
    // Co-authoring tracking
    section_version?: number;
    last_edited_by?: string;
    last_edited_by_name?: string;
}

export interface DocumentWorkspace {
    id: string;
    userId: string;
    conversationId: string;
    title: string;
    description: string;
    format: 'pdf' | 'docx' | 'pptx';
    sections: WorkspaceSection[];
    status: 'drafting' | 'ready' | 'exported';
    version: number;
    createdAt: string;
    updatedAt: string;
    exportedUrl?: string;
    
    // Workspace mode (standard document vs tender response)
    workspaceMode?: 'standard' | 'tender';
    
    // Generation context
    generationPrompt?: string;
    startingDocumentId?: string;
    startingDocumentContent?: string;
    referenceDocumentIds?: string[];  // Multiple reference documents
    referenceDocumentContents?: string[];  // Extracted text from references
    referenceDocumentStructure?: Array<{ title: string; level: number }>;
    
    // Sharing
    sharedWithUsers?: string[];
    sharedWithGroups?: string[];
    isPublic?: boolean;
    
    // Co-authoring
    activeEditors?: Array<{
        userId: string;
        userName: string;
        sectionId: string | null;
        lastSeen: string;
    }>;
}

export interface CreateWorkspaceRequest {
    title: string;
    description?: string;
    format?: 'pdf' | 'docx' | 'pptx';
    conversationId?: string;
    generationPrompt?: string;
    startingDocumentId?: string;
    referenceDocumentIds?: string[];  // Multiple reference documents
    initialSections?: Array<{
        title: string;
        content: string;
        level: number;
    }>;
}

export interface CreateAndGenerateRequest {
    title: string;
    description?: string;
    format?: 'pdf' | 'docx' | 'pptx';
    generationPrompt: string;
    startingDocumentId?: string;
    startingDocumentDescription?: string;  // User description of starting doc
    referenceDocumentIds?: string[];  // Multiple reference documents
    referenceDocumentDescriptions?: string[];  // User descriptions for each reference doc
    model?: string;
    // Data source options
    useReferencesOnly?: boolean;   // Ignore AI general knowledge, use only uploaded docs
    enableWebSearch?: boolean;     // Enable live web search for context
    allowedDomains?: string;       // Comma-separated list of allowed search domains
    // Generation mode
    generationMode?: 'ai' | 'document_intelligence';  // ai = single-shot, document_intelligence = section-by-section
    // Presentation template
    templateId?: string;           // ID of presentation template to use (for pptx)
    // Workspace mode
    mode?: 'standard' | 'tender';  // standard = normal doc generation, tender = tender response flow
    // AI Instructions (additional guidelines for AI)
    aiInstructions?: string;       // Custom instructions for AI behavior
    // Web sources for research (specific URLs to fetch from)
    webSources?: Array<{ url: string; description: string }>;
}

export interface CreateTenderWorkspaceRequest {
    title: string;
    description?: string;
    templateDocumentId: string;  // Required: The tender template
    templateDocumentDescription?: string;
    referenceDocumentIds: string[];  // Requirements, evidence, standards docs
    referenceDocumentRoles: Record<string, string>;  // Supports custom roles via text entry
    // AI instructions
    aiInstructions?: string;
    // Data source options
    useReferencesOnly?: boolean;
    enableWebSearch?: boolean;
    allowedDomains?: string;
    webSources?: Array<{ url: string; description: string }>;
}

// ============================================================================
// WORKSPACE CRUD
// ============================================================================

/**
 * Create a new workspace without generating content
 */
export async function createWorkspace(request: CreateWorkspaceRequest): Promise<DocumentWorkspace> {
    const response = await authenticatedFetch(`${API_BASE}/create`, {
        method: 'POST',
        body: JSON.stringify({
            title: request.title,
            description: request.description || '',
            format: request.format || 'docx',
            conversation_id: request.conversationId || '',
            generation_prompt: request.generationPrompt || '',
            starting_document_id: request.startingDocumentId,
            reference_document_ids: request.referenceDocumentIds || [],
            initial_sections: request.initialSections
        })
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to create workspace');
    }
    
    // Invalidate workspaces cache when a new workspace is created
    actionsMenuCache.invalidateWorkspaces();
    
    return data.workspace;
}

/**
 * Create workspace AND generate initial content using AI
 * This is the main wizard flow entry point for standard mode
 */
export async function createAndGenerateWorkspace(request: CreateAndGenerateRequest): Promise<DocumentWorkspace> {
    const response = await authenticatedFetch(`${API_BASE}/create-and-generate`, {
        method: 'POST',
        body: JSON.stringify({
            title: request.title,
            description: request.description || '',
            format: request.format || 'docx',
            generation_prompt: request.generationPrompt,
            starting_document_id: request.startingDocumentId,
            starting_document_description: request.startingDocumentDescription || '',
            reference_document_ids: request.referenceDocumentIds || [],
            reference_document_descriptions: request.referenceDocumentDescriptions || [],
            model: request.model,
            // Data source options
            use_references_only: request.useReferencesOnly || false,
            enable_web_search: request.enableWebSearch || false,
            allowed_domains: request.allowedDomains || '',
            // Generation mode
            generation_mode: request.generationMode || 'ai',
            // Presentation template
            template_id: request.templateId || null,
            // AI instructions
            ai_instructions: request.aiInstructions || '',
            // Web sources
            web_sources: request.webSources || []
        })
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to create and generate workspace');
    }
    
    // Invalidate workspaces cache when a new workspace is created
    actionsMenuCache.invalidateWorkspaces();
    
    return data.workspace;
}

/**
 * Create a tender workspace (mode=tender)
 * Does NOT generate content immediately - returns workspace for TenderBuilder to run pipeline
 */
export async function createTenderWorkspace(request: CreateTenderWorkspaceRequest): Promise<TenderWorkspace> {
    const response = await authenticatedFetch(`${API_BASE}/create-tender`, {
        method: 'POST',
        body: JSON.stringify({
            title: request.title,
            description: request.description || '',
            template_document_id: request.templateDocumentId,
            template_document_description: request.templateDocumentDescription || '',
            reference_document_ids: request.referenceDocumentIds,
            reference_document_roles: request.referenceDocumentRoles,
            // AI instructions
            ai_instructions: request.aiInstructions || '',
            // Data source options
            use_references_only: request.useReferencesOnly ?? true,
            enable_web_search: request.enableWebSearch || false,
            allowed_domains: request.allowedDomains || '',
            web_sources: request.webSources || []
        })
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to create tender workspace');
    }
    
    // Invalidate workspaces cache when a new workspace is created
    actionsMenuCache.invalidateWorkspaces();
    
    return data.workspace as TenderWorkspace;
}

/**
 * Get a specific workspace
 */
export async function getWorkspace(workspaceId: string): Promise<DocumentWorkspace> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}`);
    const data = await response.json();
    
    if (!data.success) {
        throw new Error(data.error || 'Workspace not found');
    }
    
    return data.workspace;
}

/**
 * List user's workspaces
 */
export async function listWorkspaces(limit = 50): Promise<DocumentWorkspace[]> {
    const response = await authenticatedFetch(`${API_BASE}/list?limit=${limit}`);
    const data = await response.json();
    
    return data.success ? data.workspaces : [];
}

/**
 * Delete a workspace
 */
export async function deleteWorkspace(workspaceId: string): Promise<boolean> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}`, { method: 'DELETE' });
    const data = await response.json();
    
    // Invalidate workspaces cache when a workspace is deleted
    if (data.success) {
        actionsMenuCache.invalidateWorkspaces();
    }
    
    return data.success;
}

/**
 * Update workspace status
 * Valid statuses: 'drafting' | 'ready' | 'exported'
 */
export async function updateWorkspaceStatus(
    workspaceId: string, 
    status: 'drafting' | 'ready' | 'exported'
): Promise<DocumentWorkspace | null> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
    });
    const data = await response.json();
    
    return data.success ? data.workspace : null;
}

// ============================================================================
// AI GENERATION
// ============================================================================

/**
 * Generate or regenerate document content for an existing workspace
 */
export async function generateContent(
    workspaceId: string, 
    options?: { prompt?: string; model?: string }
): Promise<DocumentWorkspace> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/generate`, {
        method: 'POST',
        body: JSON.stringify({
            prompt: options?.prompt,
            model: options?.model
        })
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to generate content');
    }
    
    return data.workspace;
}

/**
 * Extract content from an uploaded document and store it in workspace
 */
export async function extractDocumentContent(
    workspaceId: string,
    documentId: string,
    documentType: 'starting' | 'reference'
): Promise<{ contentLength: number; structure?: Array<{ title: string; level: number }> }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/extract-document`, {
        method: 'POST',
        body: JSON.stringify({
            document_id: documentId,
            document_type: documentType
        })
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to extract document content');
    }
    
    return {
        contentLength: data.content_length,
        structure: data.structure
    };
}

// ============================================================================
// SECTION OPERATIONS
// ============================================================================

/**
 * Add a section to workspace
 */
export async function addSection(
    workspaceId: string,
    section: { title: string; content: string; level?: number; order?: number }
): Promise<WorkspaceSection> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/sections`, {
        method: 'POST',
        body: JSON.stringify(section)
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to add section');
    }
    
    return data.section;
}

/**
 * Update a section
 */
export async function updateSection(
    workspaceId: string,
    sectionId: string,
    updates: { title?: string; content?: string; status?: string }
): Promise<WorkspaceSection> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/sections/${sectionId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to update section');
    }
    
    return data.section;
}

/**
 * Delete a section
 */
export async function deleteSection(
    workspaceId: string,
    sectionId: string
): Promise<boolean> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/sections/${sectionId}`, {
        method: 'DELETE'
    });
    
    const data = await response.json();
    return data.success;
}

// ============================================================================
// EXPORT
// ============================================================================

/**
 * Export workspace as document (PDF or DOCX)
 */
export async function exportWorkspace(workspaceId: string): Promise<string> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/export`, {
        method: 'POST'
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to export workspace');
    }
    
    // Build full URL for cross-origin support
    const downloadUrl = data.download_url;
    return downloadUrl.startsWith('http') ? downloadUrl : buildApiUrl(downloadUrl);
}

// ============================================================================
// SHARING
// ============================================================================

export interface WorkspaceSharingSettings {
    shared_with_users: string[];
    shared_with_groups: string[];
    is_public: boolean;
    is_owner: boolean;
    owner_id: string;
}

export interface WorkspaceWithOwnership extends DocumentWorkspace {
    is_owner: boolean;
    is_shared: boolean;
}

export interface WorkspaceUpdates {
    has_updates: boolean;
    current_version: number;
    workspace: DocumentWorkspace | null;
    changed_sections: Array<{
        id: string;
        title: string;
        edited_by: string;
        updated_at: string;
    }>;
    active_editors: Array<{
        userId: string;
        userName: string;
        sectionId: string | null;
        lastSeen: string;
    }>;
}

/**
 * Get sharing settings for a workspace
 */
export async function getWorkspaceSharing(workspaceId: string): Promise<WorkspaceSharingSettings> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/sharing`);
    const data = await response.json();
    
    if (!data.success) {
        throw new Error(data.error || 'Failed to get sharing settings');
    }
    
    return data.sharing;
}

/**
 * Update sharing settings for a workspace
 */
export async function updateWorkspaceSharing(
    workspaceId: string,
    settings: { 
        shared_with_users?: string[]; 
        shared_with_groups?: string[];
        is_public?: boolean;
    }
): Promise<DocumentWorkspace> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/sharing`, {
        method: 'PATCH',
        body: JSON.stringify(settings)
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to update sharing settings');
    }
    
    return data.workspace;
}

/**
 * List workspaces shared with the current user
 */
export async function listSharedWorkspaces(limit = 50): Promise<DocumentWorkspace[]> {
    const response = await authenticatedFetch(`${API_BASE}/shared?limit=${limit}`);
    const data = await response.json();
    
    return data.success ? data.workspaces : [];
}

/**
 * List all accessible workspaces (owned + shared)
 */
export async function listAllAccessibleWorkspaces(
    limit = 50,
    includeShared = true
): Promise<WorkspaceWithOwnership[]> {
    const response = await authenticatedFetch(
        `${API_BASE}/all?limit=${limit}&include_shared=${includeShared}`
    );
    const data = await response.json();
    
    return data.success ? data.workspaces : [];
}

// ============================================================================
// CO-AUTHORING
// ============================================================================

/**
 * Poll for workspace updates (co-authoring)
 */
export async function getWorkspaceUpdates(
    workspaceId: string,
    sinceVersion: number
): Promise<WorkspaceUpdates> {
    const response = await authenticatedFetch(
        `${API_BASE}/${workspaceId}/updates?since_version=${sinceVersion}`
    );
    const data = await response.json();
    
    if (!data.success) {
        throw new Error(data.error || 'Failed to get updates');
    }
    
    return data;
}

/**
 * Update presence (heartbeat for co-authoring)
 */
export async function updatePresence(
    workspaceId: string,
    userName: string,
    sectionId?: string
): Promise<boolean> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/presence`, {
        method: 'POST',
        body: JSON.stringify({
            user_name: userName,
            section_id: sectionId || null
        })
    });
    
    const data = await response.json();
    return data.success;
}

// ============================================================================
// TENDER MODE TYPES
// ============================================================================

export type DocumentRole = 'template' | 'requirements' | 'evidence' | 'standards' | 'commercial' | 'site_constraints' | 'external_research';

export interface WorkspaceDocument {
    id: string;
    filename: string;
    role: DocumentRole;
    description: string;
    size: number;
    uploaded_at: string;
    status: 'pending' | 'indexing' | 'indexed' | 'error';
    chunk_count: number;
    error_message?: string;
}

export interface TemplateSection {
    id: string;
    title: string;
    level: number;
    heading_path: string;
    order: number;
    expected_content_type: 'narrative' | 'table' | 'bullet' | 'diagram';
    cues: string[];
    required_fields: string[];
    placeholder_slots: Array<{
        slot_id: string;
        label: string;
        type: string;
        hints: string[];
    }>;
}

export interface Requirement {
    id: string;
    text: string;
    source_doc_id: string;
    source_chunk_id: string;
    requirement_type: 'functional' | 'non_functional' | 'compliance' | 'commercial' | 'deliverable';
    mandatory_level: 'must' | 'should' | 'may' | 'preferred';
    domain_tags: string[];
    acceptance_criteria: string;
    linked_section_ids: string[];
    evidence_chunk_ids: string[];
    coverage_status: 'not_covered' | 'partial' | 'covered';
}

export interface SectionRequirementLink {
    id: string;
    section_id: string;
    requirement_id: string;
    confidence: number;
    rationale: string;
    source: 'auto' | 'user_confirmed' | 'user_overridden';
}

export interface GapItem {
    id: string;
    section_id: string;
    requirement_id: string;
    slot_id: string;
    gap_type: 'missing_coverage' | 'missing_data' | 'missing_evidence' | 'conflict';
    description: string;
    severity: 'blocker' | 'warning' | 'info';
    status: 'open' | 'answered' | 'deferred' | 'resolved';
    suggested_sources: string[];
    question_to_user: string;
    user_answer: string;
    answered_by: string;
    answered_at: string;
}

export type TenderState = 'created' | 'ingested' | 'template_parsed' | 'requirements_parsed' | 'mapped' | 'evidence_linked' | 'gaps_analyzed' | 'drafting' | 'review_ready' | 'finalized';

export interface TenderWorkspace extends DocumentWorkspace {
    workspaceMode: 'standard' | 'tender';
    documents: WorkspaceDocument[];
    tenderState: TenderState;
    templateSchema: TemplateSection[];
    requirements: Requirement[];
    sectionRequirementLinks: SectionRequirementLink[];
    gaps: GapItem[];
    requirementsCoverage: number;
    openBlockers: number;
    numbersPolicy: 'never_invent' | 'allow_estimates_with_label';
    citationPolicy: 'required' | 'optional';
    // Pipeline caching
    lastPipelineRunAt?: string;
    documentsVersionHash?: string;
    // Background pipeline progress (for polling)
    pipelineRunId?: string;
    pipelineProgress?: number;
    pipelineCurrentStep?: string;
    pipelineError?: string | null;
    pipelineStartedAt?: string;
}

export interface PipelineStatus {
    pipelineRunId: string | null;
    pipelineProgress: number;
    pipelineCurrentStep: string;
    pipelineError: string | null;
    pipelineStartedAt: string | null;
    tenderState: string;
    isRunning: boolean;
    isComplete: boolean;
    isStale?: boolean;  // True if pipeline seems stuck (> 30 minutes old)
    templateSections?: number;
    requirements?: number;
    gaps?: number;
}

// ============================================================================
// TENDER MODE API FUNCTIONS
// ============================================================================

/**
 * Add a document to a tender workspace with role metadata
 */
export async function addTenderDocument(
    workspaceId: string,
    blobId: string,
    filename: string,
    role: DocumentRole,
    description: string = '',
    size: number = 0
): Promise<WorkspaceDocument> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/documents`, {
        method: 'POST',
        body: JSON.stringify({
            blob_id: blobId,
            filename,
            role,
            description,
            size
        })
    });
    
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.error || 'Failed to add document');
    }
    
    return data.document;
}

/**
 * Index all pending documents in the workspace
 */
export async function indexTenderDocuments(workspaceId: string): Promise<{ success: boolean; results: Array<{ doc_id: string; status: string }> }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/index`, {
        method: 'POST'
    });
    return response.json();
}

/**
 * Parse template document to extract section schema
 */
export async function parseTenderTemplate(workspaceId: string): Promise<{ success: boolean; sections: TemplateSection[] }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/parse-template`, {
        method: 'POST'
    });
    return response.json();
}

/**
 * Extract requirements from requirements documents
 */
export async function extractTenderRequirements(workspaceId: string): Promise<{ success: boolean; requirements: Requirement[] }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/extract-requirements`, {
        method: 'POST'
    });
    return response.json();
}

/**
 * Map requirements to template sections
 */
export async function mapTenderRequirements(workspaceId: string): Promise<{ success: boolean; mappings: SectionRequirementLink[] }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/map-requirements`, {
        method: 'POST'
    });
    return response.json();
}

/**
 * Link evidence to requirements
 */
export async function linkTenderEvidence(workspaceId: string): Promise<{ success: boolean; link_count: number }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/link-evidence`, {
        method: 'POST'
    });
    return response.json();
}

/**
 * Analyze gaps in requirements coverage
 */
export async function analyzeTenderGaps(workspaceId: string): Promise<{ success: boolean; gaps: GapItem[]; coverage: number; blockers: number }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/analyze-gaps`, {
        method: 'POST'
    });
    return response.json();
}

/**
 * Get gaps for workspace
 */
export async function getTenderGaps(workspaceId: string, sectionId?: string): Promise<{ success: boolean; gaps: GapItem[]; requirements_coverage: number; open_blockers: number }> {
    const url = sectionId 
        ? `${API_BASE}/${workspaceId}/tender/gaps?section_id=${sectionId}`
        : `${API_BASE}/${workspaceId}/tender/gaps`;
    const response = await authenticatedFetch(url);
    return response.json();
}

/**
 * Resolve a gap by providing an answer
 */
export async function resolveTenderGap(
    workspaceId: string,
    gapId: string,
    answer: string,
    status: 'answered' | 'deferred' | 'resolved' = 'answered'
): Promise<{ success: boolean }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/gaps/${gapId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ answer, status })
    });
    return response.json();
}

/**
 * Get requirements catalog
 */
export async function getTenderRequirements(workspaceId: string): Promise<{ success: boolean; requirements: Requirement[] }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/requirements`);
    return response.json();
}

/**
 * Get section-requirement mappings
 */
export async function getTenderMappings(workspaceId: string): Promise<{ success: boolean; mappings: SectionRequirementLink[]; template_schema: TemplateSection[] }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/mappings`);
    return response.json();
}

/**
 * Update a section-requirement mapping
 */
export async function updateTenderMapping(
    workspaceId: string,
    requirementId: string,
    sectionId: string,
    action: 'add' | 'remove'
): Promise<{ success: boolean }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/mappings`, {
        method: 'PATCH',
        body: JSON.stringify({
            requirement_id: requirementId,
            section_id: sectionId,
            action
        })
    });
    return response.json();
}

/**
 * Draft a specific section with streaming
 */
/**
 * Draft a tender section with AI (streaming).
 * 
 * @param workspaceId - Workspace ID
 * @param sectionId - Section to draft
 * @param instruction - Optional user instruction for customizing the draft
 * @param onChunk - Callback for each streamed chunk
 * @returns Full generated content
 */
export async function draftTenderSection(
    workspaceId: string,
    sectionId: string,
    onChunk: (chunk: string) => void,
    instruction?: string
): Promise<string> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/sections/${sectionId}/draft`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: instruction ? JSON.stringify({ instruction }) : undefined
    });
    
    if (!response.body) {
        throw new Error('No response body');
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullContent = '';
    
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        fullContent += chunk;
        onChunk(chunk);
    }
    
    return fullContent;
}

// ============================================================================
// BACKGROUND PIPELINE API (Polling-based - no SSE timeout issues)
// ============================================================================

/**
 * Start the tender pipeline in background mode.
 * Returns immediately. Use pollPipelineStatus() to track progress.
 * 
 * @param workspaceId - Workspace ID
 * @param force - Force re-run even if no changes (default: false)
 * @returns Run ID to track this pipeline execution
 */
export async function startTenderPipeline(
    workspaceId: string,
    force: boolean = false
): Promise<{ success: boolean; run_id: string; message: string }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/start-pipeline`, {
        method: 'POST',
        body: JSON.stringify({ force })
    });
    return response.json();
}

/**
 * Get current pipeline status (for polling).
 * Call this every 2-3 seconds until isComplete or pipelineError.
 * 
 * @param workspaceId - Workspace ID
 * @returns Current pipeline status
 */
export async function getPipelineStatus(workspaceId: string): Promise<PipelineStatus> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/pipeline-status`, {
        method: 'GET'
    });
    return response.json();
}

/**
 * Cancel/reset a stale or stuck pipeline.
 * This clears the running state and allows restarting.
 * 
 * @param workspaceId - Workspace ID
 * @returns Success indicator
 */
export async function cancelTenderPipeline(workspaceId: string): Promise<{ success: boolean; message: string }> {
    const response = await authenticatedFetch(`${API_BASE}/${workspaceId}/tender/cancel-pipeline`, {
        method: 'POST'
    });
    return response.json();
}

/**
 * Run tender pipeline with automatic polling.
 * This is a convenience wrapper that starts the pipeline and polls for completion.
 * 
 * @param workspaceId - Workspace ID
 * @param onProgress - Callback for progress updates (called every poll)
 * @param force - Force re-run even if no changes
 * @param pollIntervalMs - Polling interval in ms (default: 2000)
 */
export async function runTenderPipelineWithPolling(
    workspaceId: string,
    onProgress: (status: PipelineStatus) => void,
    force: boolean = false,
    pollIntervalMs: number = 10000
): Promise<PipelineStatus> {
    console.log('[Pipeline] Starting background pipeline...');
    
    // Start the pipeline
    const startResult = await startTenderPipeline(workspaceId, force);
    if (!startResult.success) {
        throw new Error(startResult.message || 'Failed to start pipeline');
    }
    
    console.log('[Pipeline] Pipeline started with run_id:', startResult.run_id);
    
    // Poll for completion
    return new Promise((resolve, reject) => {
        const pollInterval = setInterval(async () => {
            try {
                const status = await getPipelineStatus(workspaceId);
                
                // Notify progress
                onProgress(status);
                
                // Check if complete or error
                if (status.pipelineError) {
                    clearInterval(pollInterval);
                    reject(new Error(status.pipelineError));
                    return;
                }
                
                if (status.isComplete) {
                    clearInterval(pollInterval);
                    console.log('[Pipeline] Pipeline completed!');
                    resolve(status);
                    return;
                }
                
                console.debug(`[Pipeline] Progress: ${status.pipelineProgress}% - ${status.pipelineCurrentStep}`);
                
            } catch (error) {
                clearInterval(pollInterval);
                reject(error);
            }
        }, pollIntervalMs);
        
        // Safety timeout - 10 minutes max
        setTimeout(() => {
            clearInterval(pollInterval);
            reject(new Error('Pipeline timeout after 10 minutes'));
        }, 10 * 60 * 1000);
    });
}
